"use client";

import { useEffect, useState } from "react";
import { Sun, Moon, Sunset } from "lucide-react";
import { cn } from "@/lib/utils";

type Theme = "light" | "dark" | "sepia" | "system";

const THEMES: { id: Theme; icon: typeof Sun; label: string }[] = [
  { id: "light", icon: Sun, label: "Light" },
  { id: "dark", icon: Moon, label: "Dark" },
  { id: "sepia", icon: Sunset, label: "Sepia" },
];

const THEME_CHANGE_EVENT = "nc-theme-changed";

function applyDomTheme(value: Theme) {
  document.documentElement.setAttribute(
    "data-theme",
    value === "system" ? "system" : value
  );
}

function readStoredTheme(): Theme {
  try {
    return (localStorage.getItem("nc-theme") as Theme | null) || "light";
  } catch {
    return "light";
  }
}

/**
 * Theme control for marketing + app chrome.
 *
 * Reads localStorage only after mount so the first client render matches the
 * server ("light"). Updating from useSyncExternalStore during hydration can
 * schedule setState on a fiber that has not committed yet (React 19 warning).
 */
export function ThemeSwitcher({
  compact = false,
  iconOnly = false,
}: {
  compact?: boolean;
  /** Segmented control, but icons only (no text labels) — saves nav space. */
  iconOnly?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    const initial = readStoredTheme();
    setTheme(initial);
    applyDomTheme(initial);
    setMounted(true);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== "nc-theme" && e.key !== null) return;
      const next = readStoredTheme();
      setTheme(next);
      applyDomTheme(next);
    };
    const onLocal = () => {
      const next = readStoredTheme();
      setTheme(next);
      applyDomTheme(next);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(THEME_CHANGE_EVENT, onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(THEME_CHANGE_EVENT, onLocal);
    };
  }, []);

  function switchTheme(newTheme: Theme) {
    try {
      localStorage.setItem("nc-theme", newTheme);
    } catch {
      // ignore
    }
    applyDomTheme(newTheme);
    setTheme(newTheme);
    window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
  }

  // Stable placeholder until mount: matches SSR (light selected).
  const active = mounted ? theme : "light";

  if (compact) {
    const order: Theme[] = ["light", "dark", "sepia"];
    const nextTheme = order[(order.indexOf(active) + 1) % order.length]!;
    const CurrentIcon = THEMES.find((t) => t.id === active)?.icon || Sun;

    return (
      <button
        type="button"
        onClick={() => switchTheme(nextTheme)}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        title={`Theme: ${active}`}
        disabled={!mounted}
      >
        <CurrentIcon className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="flex gap-1 bg-muted rounded-lg p-0.5">
      {THEMES.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => switchTheme(id)}
          title={iconOnly ? label : undefined}
          aria-label={iconOnly ? label : undefined}
          disabled={!mounted}
          className={cn(
            "flex items-center rounded-md text-xs font-medium transition-colors",
            iconOnly ? "px-2 py-1.5" : "gap-1.5 px-3 py-1.5",
            active === id
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Icon className="w-3.5 h-3.5" />
          {!iconOnly && label}
        </button>
      ))}
    </div>
  );
}
